import { Report } from '../types/report';
import { Teacher } from '../types/teacher';
import axiosInstance from '../api/apiInterceptor';
import { AxiosResponse } from 'axios';
import { TEACHER_API_ENDPOINT, REPORT_API_ENDPOINT, REPORT_API_ENDPOINT_EXCEPTION, REPORT_SUMMARY_API_ENDPOINT } from "../../../constants/constants"
import { getUserDetails } from "../../../lib/getUserDetails";

interface Payload {
    userId?: number;
    startDate?: string;
    endDate?: string;
    schoolIds?: any;
    classSectionId?: number;
    isCumulative?: boolean
    [key: string]: any; // Allow additional query parameters
}

const extractErrorMessage = (error: any): string => {
    if (error?.response?.data?.message) {
        return error.response.data.message;
    }

    if (error?.response?.data?.error) {
        return error.response.data.error;
    }

    if (error?.response?.data?.details) {
        return error.response.data.details;
    }

    if (error?.message) {
        return error.message;
    }

    return 'An unexpected error occurred';
};

class ApiError extends Error {
    public originalError: any;
    public statusCode?: number;

    constructor(message: string, originalError: any) {
        super(message);
        this.name = 'ApiError';
        this.originalError = originalError;
        this.statusCode = originalError?.response?.status;
    }
}

export const fetchUserDetailApi = async (payload: Payload): Promise<Report[]> => {
    try {
        const response: AxiosResponse<any> = await axiosInstance.get(`${TEACHER_API_ENDPOINT}/${payload.userId}`);
        return response.data.data;
    } catch (error: any) {
        console.error('Error fetching Teachers:', error);
        const message = extractErrorMessage(error);
        throw new ApiError(message, error);
    }
};

export const fetchReportApi = async (payload: Payload): Promise<Report[]> => {
    // const userData = getUserToken();
    // const roleIds = userData?.roleIds[0];
    const userDetail = getUserDetails();
    const defaultSchoolId = userDetail?.[0]?.relatedEntity?.schools?.id;

    try {
        // Build query parameters from payload
        let queryParams: any = new URLSearchParams();
        // Add all payload properties as query parameters
        Object.entries(payload).forEach(([key, value]) => {
            if (key === "classSectionIds" && value !== undefined && value !== null) {
                // Handle classSectionIds as array - append each ID as separate parameter
                if (Array.isArray(value)) {
                    value.forEach((id: number) => {
                        queryParams.append(key, id.toString());
                    });
                } else {
                    queryParams.append(key, value.toString());
                }
            } else if (value !== undefined && value !== null) {
                queryParams.append(key, value.toString());
            }
        });

        // Use default schoolId if not provided in payload
        if (!payload.schoolId) {
            queryParams.append('schoolIds', defaultSchoolId?.toString() || '');
        }

        //queryParams=null;

        // Construct URL with query parameters
        const url = queryParams.toString()
            ? `${REPORT_API_ENDPOINT}?${queryParams?.toString()}`
            : REPORT_API_ENDPOINT;

        const response: AxiosResponse<any> = await axiosInstance.get(url);
        console.log("Report response", response.data?.data?.data || [])
        return response.data?.data?.data || [];

    } catch (error: any) {
        console.error('Error fetching Reports:', error);
        const message = extractErrorMessage(error);
        throw new ApiError(message, error);
    }
};

export const fetchReportSummaryApi = async (payload: Payload): Promise<Report[]> => {
    // const userData = getUserToken();
    // const roleIds = userData?.roleIds[0];
    const userDetail = getUserDetails();
    const defaultSchoolId = userDetail?.[0]?.relatedEntity?.schools?.id;

    try {
        // Build query parameters from payload
        let queryParams: any = new URLSearchParams();
        // Add all payload properties as query parameters
        Object.entries(payload).forEach(([key, value]) => {
            if (key === "classSectionIds" && value !== undefined && value !== null) {
                // Handle classSectionIds as array - append each ID as separate parameter
                if (Array.isArray(value)) {
                    value.forEach((id: number) => {
                        queryParams.append(key, id.toString());
                    });
                } else {
                    queryParams.append(key, value.toString());
                }
            } else if (value !== undefined && value !== null) {
                queryParams.append(key, value.toString());
            }
        });

        // Use default schoolId if not provided in payload
        if (!payload.schoolId) {
            queryParams.append('schoolIds', defaultSchoolId?.toString() || '');
        }

        //queryParams=null;

        // Construct URL with query parameters
        const url = queryParams.toString()
            ? `${REPORT_SUMMARY_API_ENDPOINT}?${queryParams?.toString()}`
            : REPORT_SUMMARY_API_ENDPOINT;

        const response: AxiosResponse<any> = await axiosInstance.get(url);
        console.log("Report response", response.data?.data?.data || [])
        return response.data?.data || [];

    } catch (error: any) {
        console.error('Error fetching Reports:', error);
        const message = extractErrorMessage(error);
        throw new ApiError(message, error);
    }
};

export const fetchAdminReportSummaryApi = async (payload: Payload): Promise<any> => {
    try {
        const { ...params } = payload as Record<string, unknown>;
 
        // Axios will serialize `params` into the query string and will skip null/undefined
        const response: AxiosResponse<any> = await axiosInstance.get(
            REPORT_SUMMARY_API_ENDPOINT,
            { params }
        );
 
        return response.data?.data || [];
    } catch (error: any) {
        console.error('Error fetching Admin Reports:', error);
        const message = extractErrorMessage(error);
        throw new ApiError(message, error);
    }
};
 
 
export const fetchAdminTopMealWiseSummaryApi = async (payload: Payload): Promise<any> => {
    try {
        const { ...params } = payload as Record<string, unknown>;
        // Axios will serialize params into the query string and will skip null/undefined
        const response: AxiosResponse<any> = await axiosInstance.get(
            REPORT_SUMMARY_API_ENDPOINT,
            { params }
        );
        return response.data?.data || [];
    } catch (error: any) {
        console.error('Error fetching Top Meal Wise Summary:', error);
        const message = extractErrorMessage(error);
        throw new ApiError(message, error);
    }
};

export const fetchExceptionReportApi = async (payload: Payload): Promise<Report[]> => {
    // const userData = getUserToken();
    // const roleIds = userData?.roleIds[0];
    const userDetail = getUserDetails();
    const defaultSchoolId: any = userDetail?.[0]?.relatedEntity?.schools?.id;
    try {
        // Build query parameters from payload
        let queryParams: any = new URLSearchParams();
        // Add all payload properties as query parameters
        Object.entries(payload).forEach(([key, value]) => {
            if (key === "classSectionIds" && value !== undefined && value !== null) {
                // Handle classSectionIds as array - append each ID as separate parameter
                if (Array.isArray(value)) {
                    value.forEach((id: number) => {
                        queryParams.append(key, id.toString());
                    });
                } else {
                    queryParams.append(key, value.toString());
                }
            } else if (value !== undefined && value !== null) {
                queryParams.append(key, value.toString());
            }
        });

        // Use default schoolId if not provided in payload
        if (!payload.schoolIds && defaultSchoolId != null) {
            queryParams.append('schoolIds', defaultSchoolId.toString());
        }

        // Construct URL with query parameters
        const url = queryParams.toString()
            ? `${REPORT_API_ENDPOINT_EXCEPTION}?${queryParams?.toString()}`
            : REPORT_API_ENDPOINT_EXCEPTION;

        const response: AxiosResponse<any> = await axiosInstance.get(url);
        return response.data.data;
    } catch (error: any) {
        console.error('Error fetching Reports:', error);
        const message = extractErrorMessage(error);
        throw new ApiError(message, error);
    }
};

export const fetchUserRoleApi = async () => {
    const url = `user-roles?limit=1000`;
    try {
        const response: AxiosResponse<any> = await axiosInstance.get(`${url}`);
        return response.data;
    } catch (error: any) {
        console.log('Error fetch user role:', error);
        const message = extractErrorMessage(error);
        throw new ApiError(message, error);
    }
}

export const addTeacherApi = async (TeacherData: Omit<Teacher, 'id'>): Promise<Teacher> => {
    try {
        const response: AxiosResponse<Teacher> = await axiosInstance.post(TEACHER_API_ENDPOINT, TeacherData);
        return response.data;
    } catch (error: any) {
        console.error('Error adding Teacher:', error);
        const message = extractErrorMessage(error);
        throw new ApiError(message, error);
    }
};



export const updateTeacherApi = async (Teacher: Teacher): Promise<Teacher> => {
    try {
        const response: AxiosResponse<Teacher> = await axiosInstance.patch(`${TEACHER_API_ENDPOINT}/${Teacher.id}`, Teacher);
        return response.data;
    } catch (error: any) {
        console.error('Error updating Teacher:', error);
        const message = extractErrorMessage(error);
        throw new ApiError(message, error);
    }
};

export const deleteTeacherApi = async (id: number): Promise<number> => {
    try {
        await axiosInstance.delete(`${TEACHER_API_ENDPOINT}/${id}`);
        return id;
    } catch (error: any) {
        console.error('Error deleting Teacher:', error);
        const message = extractErrorMessage(error);
        throw new ApiError(message, error);
    }
};